import { useEffect, useState } from "react"
import { FileText, Loader2 } from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/modules/shared/ui/components/alert-dialog"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { documentService } from "@/modules/documents/infrastructure/document-service"
import type { DocumentDto } from "@/modules/documents/entities/DocumentDto"
import { useDocPushContext } from "@/modules/sage300/context/DocPushContext"
import { sage300DocumentService } from "@/modules/sage300/infrastructure/sage300DocumentService"

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string
  employeeId: string
  employeeName: string
}

export function PushDocumentsToSageDialog({
  open,
  onOpenChange,
  organizationId,
  employeeId,
  employeeName,
}: Props) {
  const [isFetching, setIsFetching] = useState(false)
  const [isPushing, setIsPushing] = useState(false)
  const [documents, setDocuments] = useState<DocumentDto[]>([])
  const [fetchError, setFetchError] = useState<string | null>(null)
  const { addPush } = useDocPushContext()

  useEffect(() => {
    if (!open) return

    setDocuments([])
    setFetchError(null)
    setIsFetching(true)

    documentService
      .getEmployeeDocuments(organizationId, employeeId)
      .then(setDocuments)
      .catch(() => {
        setFetchError("Failed to load documents. Please try again.")
      })
      .finally(() => {
        setIsFetching(false)
      })
  }, [open, organizationId, employeeId])

  const handlePushAll = async () => {
    setIsPushing(true)
    try {
      const results = await Promise.allSettled(
        documents.map((doc) =>
          sage300DocumentService.pushDocument(organizationId, employeeId, {
            documentId: doc.id,
            subject: doc.fileName,
          }),
        ),
      )

      results.forEach((result, index) => {
        const doc = documents[index]
        if (
          result.status === "fulfilled" &&
          result.value.success &&
          result.value.pushId
        ) {
          addPush({
            pushId: result.value.pushId,
            label: doc.fileName,
            organizationId,
            employeeId,
            status: "Queued",
          })
        } else {
          const errorMessage =
            result.status === "fulfilled"
              ? result.value.message
              : "Failed to enqueue document push"
          addPush({
            pushId: `failed-${doc.id}-${Date.now()}`,
            label: doc.fileName,
            organizationId,
            employeeId,
            status: "Failed",
            errorMessage,
          })
        }
      })

      onOpenChange(false)
    } finally {
      setIsPushing(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Push Documents to Sage 300</AlertDialogTitle>
          <AlertDialogDescription>
            The following documents will be sent to{" "}
            <span className="font-medium text-foreground">{employeeName}</span>
            {"'s Sage 300 self-service portal:"}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {isFetching && (
          <div className="flex items-center justify-center py-6">
            <Spinner className="h-5 w-5" />
          </div>
        )}

        {!isFetching && fetchError && (
          <p className="text-sm text-destructive">{fetchError}</p>
        )}

        {!isFetching && !fetchError && documents.length === 0 && (
          <p className="text-sm text-muted-foreground py-2">
            No documents found for this employee.
          </p>
        )}

        {!isFetching && !fetchError && documents.length > 0 && (
          <ul className="max-h-48 overflow-y-auto space-y-1.5 py-1">
            {documents.map((doc) => (
              <li
                key={doc.id}
                className="flex items-center gap-2 text-sm text-foreground"
              >
                <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate">{doc.fileName}</span>
              </li>
            ))}
          </ul>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPushing}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              void handlePushAll()
            }}
            disabled={isPushing || isFetching || documents.length === 0}
          >
            {isPushing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Pushing…
              </>
            ) : (
              `Push All (${documents.length} document${documents.length !== 1 ? "s" : ""})`
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
